import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { ItemProvider, useItemProviderContext } from '@common/react/components/Core/ItemProvider/ItemProvider';
import { ItemsProvider } from '@common/react/components/Core/ItemsProvider/ItemsProvider';
import { Lang } from '@common/typescript/objects/Lang';
import Loader from '@common/react/components/Core/LoadingProvider/Loader';
import { BreadCrumbs } from '@common/react/components/UI/BreadCrumbs/BreadCrumbs';
import ServerPageProvider from '@common/react/components/UI/lang/SeverPageProviderWithLang/ServerpageProviderWithLang';

import { FaqPage } from '@commonTuna/react/objects/FaqPage';
import { Page } from '@commonTuna/react/objects/Page';
import { Faq } from '@commonTuna/react/objects/Faq';

import { capitalizeFirstLetter } from '@app/components/Utils';
import '@app/scss/pages/faqPage.scss';
import ShareLinks from '@app/components/UI/ShareLinks/ShareLinks';
import ServiceCard from '@app/components/Pages/Service/ServiceCard';

import ContactUsForm from '@app/components/Forms/ContactUs/ContactUsForm';
import CollapseService from '@app/components/Pages/Faq/CollapseService';
import { ApplicationState } from '@app/store';

const SingleFaqInner: React.FC = () => {
	const context = useItemProviderContext<Faq>();

	if (!context.state) throw 'Need ItemProvider context!';

	const { state: { item }, actions: { load } } = context;

	const { i18n, t } = useTranslation();
	const langString: 'Es' | 'En' = capitalizeFirstLetter(i18n.language);

	const { page } = useParams<{ page }>();
	const location = useLocation();
	const [skipLoad, setSkipLoad] = React.useState(true);
	const lang = useSelector((state: ApplicationState) => state.login.lang);

	React.useEffect(() => {
		const newPath = lang === Lang.Es ? `/es/faq/${item?.pathEs}` : `/faq/${item?.pathEn}`;
		if (!skipLoad && newPath !== location.pathname) {
			load({ fullPath: page });
		}
		setSkipLoad(false);
	}, [location.pathname]);

	return <>
		<div className="clearfix">
			<BreadCrumbs links={[{ title: t('seo.faqPage.title'), path: t('seo.faqPage.link') }]} />
			<ShareLinks />
		</div>
		<div className="tagline text-center">
			<h1>{item[`question${langString}`]}</h1>
		</div>
		<div className="faq-page__answer clearfix external-html">
			<p>
				<div dangerouslySetInnerHTML={{ __html: item[`answer${langString}`] }} />
			</p>
		</div>
		<ItemsProvider
			type="faq"
			loadRequest="faqViewListRemote"
			items={item?.faqs?.map((q) => q.child)}
			unhandledFilters={{ parentId: item.id }}
			skipInitLoad={!!item?.faqs}
		>
			{({ state: { items } }) => <CollapseService items={items} langName={langString} />}
		</ItemsProvider>
		{item.pages?.length > 0 ? <div className="sub-pages__line" /> : null}
		<div className="sub-pages row">
			{item.pages?.map((faqPage: FaqPage) => {
				const subPage: Page = faqPage.page;
				return <ServiceCard key={subPage.id} page={subPage} />;
			})}
		</div>
		<div className="contacts-page__form">
			<div className="inner-page__headline text-center">
				<h2>{t('site.Schedule an Appointment Now')}</h2>
			</div>
			<ContactUsForm className="general-form" />
		</div>
	</>;
};

const SingleFaq: React.FC = () => {
	const { path } = useParams<{ path }>();

	return (
		<ServerPageProvider
			path="faq"
			inner={(serverPage) =>
				<ItemProvider
					id={serverPage?.item?.id || 0}
					type="faq"
					readonly
					additionalParams={{ fullPath: path, withPages: true, published: true }}
					loadRequest="getFaqView"
					item={serverPage?.item}
					skipInitLoad={!!serverPage?.item}
				>
					{({ state: { itemLoading } }) => <div className="inner-page faq-page container">
						{itemLoading ? <Loader /> : <SingleFaqInner />}
					</div>}
				</ItemProvider>
			}
		/>
	);
};

export default SingleFaq;
